<script setup lang="ts">
import { computed } from "vue";

import { metric_help } from "@/assets/i18n/en.json";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import type { MetricDto } from "@/dto";

const { metric } = defineProps<{ metric: MetricDto }>();

const hasHelp = computed(() => metric.name.toLowerCase() in metric_help);
const help = computed(() => {
  const i18n: { math: string; description: string } | undefined = (metric_help as any)[
    metric.name.toLowerCase()
  ];
  return { math: i18n?.math ?? "", description: i18n?.description ?? "" };
});
</script>

<template>
  <FloatingTooltip v-if="hasHelp" class="metric-tooltip" strategy="absolute">
    <i class="icon icon-info-circle-2" />
    <template #tooltipContent>
      <div class="metric-tooltip-content">
        <div v-if="help.math.length > 0" class="math">
          <MarkdownWidget :source="help.math" :use-highlight="false" />
        </div>
        <div class="text">
          <div class="title text-base-medium">Why it matters:</div>
          <div class="description text-base-regular">
            <MarkdownWidget :source="help.description" :use-highlight="false" />
          </div>
        </div>
      </div>
    </template>
  </FloatingTooltip>
</template>

<style scoped>
.metric-tooltip {
  cursor: help;

  .icon {
    color: var(--border-secondary);
    vertical-align: middle;
  }
}

.metric-tooltip-content {
  display: flex;
  flex-direction: row;
  gap: var(--numbers-12);

  .math {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--numbers-4);
    border-radius: var(--numbers-6);
    background-color: #242428;
    color: var(--content-disable);
  }

  .title {
    color: var(--content-disable);
  }

  .description {
    color: var(--content-primary-inverse);
  }
}
</style>
